import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  enUS,
  frFR,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { Flex } from 'src/components/basic/Flex';
import ProfileCard from 'src/components/basic/ProfileCard';
import { CustomLoadingOverlay } from 'src/components/dataGrid/CustomLoadingOverlay';
import { CustomNoRowsOverlay } from 'src/components/dataGrid/CustomNoRowsOverlay';
import { theme } from 'src/constants/theme';
import { handleNotification } from 'src/helpers/notifications';
import { useAppDispatch } from 'src/hooks/store';
import { useOrganizationsForNormaAdmin } from 'src/hooks/store/organization.hooks';
import {
  activateAccount,
  deactivateAccount,
  deleteAccount,
  updateUserCompanyRole,
  updateUserRole,
} from 'src/slices/auth';
import { User, UserForAdmin, UserJobType } from 'src/types/auth.type';
import { Organization } from 'src/types/organization.type';
import SearchFormField from '../forms/SearchFormField';

type SubscriptionType =
  | 'manual'
  | 'automatic'
  | 'free'
  | 'freeTrialAutomatic'
  | 'freeTrialManual'
  | 'none';

type GridRowType = Omit<UserForAdmin, 'profilePictureUrl'> & {
  id: string;
  avatar?: string;
  organizations: string;
  numberOfConnections: number;
  isActiveAccount: boolean;
  jobType?: UserJobType;
  organizationSubscriptionStatus?: string;
};
interface Props {
  users: UserForAdmin[];
  setTabIndex: (index: number) => void;
  setSelectedOrganization: (organization: Organization | null) => void;
}
export const AdminUsers: React.FC<Props> = ({
  users,
  setTabIndex,
  setSelectedOrganization,
}) => {
  const dispatch = useAppDispatch();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { t, i18n } = useTranslation();

  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([]);
  const [clickedUserId, setClickedUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isDialogOpened, setDialogOpened] = useState<boolean>(false);

  const [deleteUserDialog, setDeleteUserDialog] = useState<string>('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const selectedUsers = useMemo(
    () =>
      selectedUsersIds
        .map((id) => users.find((user) => user._id === id))
        .filter((user): user is UserForAdmin => !!user),
    [selectedUsersIds, users],
  );

  const clickedUser = useMemo(
    () => users.find((user) => user._id === clickedUserId),
    [clickedUserId, users],
  );

  const filteredUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName} ${user.company}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const getSubscriptionTypeString = (
    organization: Organization,
  ): SubscriptionType => {
    if (organization.subscription?.isFreeSubscription) {
      return 'free';
    }
    if (organization.subscription?.isFreeTrial) {
      if (organization.subscription?.isAutomaticSubscriptionPaymentActive) {
        return 'freeTrialAutomatic';
      }
      return 'freeTrialManual';
    }
    if (organization.subscription?.isPaying) {
      return 'manual';
    }
    if (organization.subscription?.isAutomaticSubscriptionPaymentActive) {
      return 'automatic';
    }
    return 'none';
  };

  const organizationsForNormaAdmin = useOrganizationsForNormaAdmin();

  const rows: GridRowType[] = filteredUsers.map((user) => {
    const userOrganizations = organizationsForNormaAdmin.filter(
      (organization) =>
        organization?.members?.some((member) => member._id === user._id),
    );

    const subscriptionStatus =
      userOrganizations.length > 0
        ? userOrganizations[0] &&
          getSubscriptionTypeString(userOrganizations[0])
        : 'none';

    const organizationSubscriptionStatus =
      subscriptionStatus !== 'none'
        ? t(`pages.Admin.subscription.tableColumnHeaders.${subscriptionStatus}`)
        : t('pages.Admin.users.noSubscriptionStatus');

    const organizationDisplay =
      userOrganizations.length > 1
        ? `${userOrganizations[0]?.name} + ${userOrganizations.length - 1} ${t(
            'pages.Admin.users.more',
            {
              count: userOrganizations.length - 1,
            },
          )}`
        : userOrganizations[0]?.name || t('pages.Admin.users.noOrganization');

    return {
      ...user,
      id: user._id,
      organizations: organizationDisplay,
      lastConnection: user.stats?.lastConnection ?? user.createdAt,
      numberOfConnections: user.stats?.numberOfConnections ?? 0,
      isAdmin: user.isAdmin,
      isActiveAccount: !user.disabled,
      jobType: user.jobType,
      organizationSubscriptionStatus,
    };
  });

  const getMaxColumnWidth = (field: keyof GridRowType) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 20;

    context.font = '14px Montserrat, "Public Sans", sans-serif';

    const maxLength = Math.max(
      ...rows.map((user) => {
        const text = (user as any)[field]?.toString() || '';
        return context.measureText(text).width;
      }),
    );
    return maxLength + 20;
  };

  const columns: GridColDef<GridRowType>[] = [
    {
      field: 'firstName',
      headerName: t('common.firstName'),
      minWidth: getMaxColumnWidth('firstName'),
    },
    {
      field: 'lastName',
      headerName: t('common.lastName'),
      minWidth: getMaxColumnWidth('lastName'),
    },
    {
      field: 'email',
      headerName: t('common.email'),
      minWidth: getMaxColumnWidth('email'),
    },
    {
      field: 'organizations',
      headerName: t('pages.Admin.tabs.organizations'),
      minWidth: getMaxColumnWidth('organizations'),
    },
    {
      field: 'organizationSubscriptionStatus',
      headerName: t('pages.Admin.organization.columnHeader.subscriptionStatus'),
      minWidth: getMaxColumnWidth('organizationSubscriptionStatus'),
    },
    {
      field: 'jobType',
      headerName: t('user.jobType.label'),
      renderCell: (cellValues) =>
        cellValues.row.jobType
          ? t(`user.jobType.options.${cellValues.row.jobType}`)
          : '',
      minWidth: getMaxColumnWidth('jobType'),
    },
    {
      field: 'isActiveAccount',
      headerName: t('pages.Admin.activeAccount'),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) =>
        cellValues.row.isActiveAccount ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CheckIcon
              color="success"
              style={{ marginLeft: theme.spacing(0.5) }}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CloseIcon
              color="error"
              style={{ marginLeft: theme.spacing(0.5) }}
            />
          </div>
        ),
      minWidth: 110,
    },
    {
      field: 'totalVictimes',
      headerName: t('pages.Admin.totalVictimes'),
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.victimesCount ?? 0,
      renderCell: (cellValues) => (
        <Typography>{cellValues.row.victimesCount || 0}</Typography>
      ),
    },
    {
      field: 'isAdmin',
      headerName: t('pages.Admin.isAdmin'),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) =>
        cellValues.row.isAdmin ? (
          <AdminPanelSettingsIcon color="primary" />
        ) : (
          <div>{t('pages.Admin.noAdmin')}</div>
        ),
      minWidth: 130,
    },
    {
      field: 'registrationDate',
      headerName: t('pages.Admin.registrationDate'),
      headerAlign: 'center',
      align: 'center',
      minWidth: 140,
      valueGetter: (params) => new Date(params.row.createdAt),
      renderCell: (cellValues) => (
        <Typography>
          {new Date(cellValues.row.createdAt).toLocaleDateString()}
        </Typography>
      ),
    },
  ];

  const adminDeleteAccount = (userId: string) => {
    setDeleteUserDialog(userId);
    setOpenDeleteDialog(true);
  };

  const dialogAdminDeleteAccount = async () => {
    if (deleteUserDialog !== '') {
      await dispatch(deleteAccount(deleteUserDialog));
      setOpenDeleteDialog(false);
      setDeleteUserDialog('');
    }
  };

  const updateRole = (userId: string, isAdmin: boolean) => {
    dispatch(updateUserRole({ userId, isAdmin }));
  };

  const adminActivateAccount = (userId: string) => {
    dispatch(activateAccount(userId));
  };

  const adminDeactivateAccount = (userId: string) => {
    dispatch(deactivateAccount(userId));
  };

  const updateCompanyRole = async (
    userId: string,
    dto: { company: string; role?: string; jobType?: UserJobType },
  ) => {
    if (dto.company) {
      await handleNotification(
        dispatch(
          updateUserCompanyRole({
            userId,
            ...dto,
          }),
        ),
        t('pages.Admin.successUpdateUser'),
        t('pages.Admin.errorUpdateUser'),
        enqueueSnackbar,
        closeSnackbar,
      );
    }
  };

  const onClickRow = (params: GridRowParams<GridRowType>) => {
    setDialogOpened(true);
    setClickedUserId(params.row._id);
  };

  const displayProfileCard = (user: User) => (
    <ProfileCard
      user={user}
      updateRole={updateRole}
      adminDeleteAccount={adminDeleteAccount}
      updateCompanyRole={updateCompanyRole}
      activateAccount={adminActivateAccount}
      deactivateAccount={adminDeactivateAccount}
      setTabIndex={setTabIndex}
      setSelectedOrganization={setSelectedOrganization}
      closeProfileCard={() => setDialogOpened(false)}
    />
  );
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SearchFormField
        placeholderKey={t('pages.Admin.users.search')}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sx={{ marginBottom: 1, marginTop: 1, width: 400 }}
      />
      <DataGrid
        sx={{
          cursor: 'default',
          boxShadow:
            'rgba(145, 158, 171, 0.24) 0px 0px 2px 0px, rgba(145, 158, 171, 0.24) 0px 16px 32px -4px',
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus, \
           &.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
            {
              outline: 'none',
            },
        }}
        localeText={
          i18n.language === 'en'
            ? enUS.components.MuiDataGrid.defaultProps.localeText
            : frFR.components.MuiDataGrid.defaultProps.localeText
        }
        components={{
          LoadingOverlay: CustomLoadingOverlay,
          NoRowsOverlay: () => (
            <CustomNoRowsOverlay text={t('pages.Admin.noUsers')} />
          ),
          NoResultsOverlay: CustomNoRowsOverlay,
        }}
        loading={false}
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        onRowClick={onClickRow}
        onRowSelectionModelChange={(gridUsers) =>
          setSelectedUsersIds(gridUsers.map((gridUser) => gridUser.toString()))
        }
        pageSizeOptions={[10, 20, 50]}
      />
      <Flex
        sx={{
          marginTop: 3,
          marginBottom: 1,
          justifyContent: 'flex-end',
          marginX: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setDialogOpened(true);
          }}
          disabled={selectedUsers?.length === 0}
        >
          {t('pages.Admin.showUsers')}
        </Button>
      </Flex>
      <Dialog
        open={Boolean(
          isDialogOpened && (selectedUsers?.length !== 0 || clickedUser),
        )}
        onClose={() => {
          setDialogOpened(false);
          setSelectedUsersIds([]);
          setClickedUserId(null);
        }}
      >
        {clickedUser ? (
          displayProfileCard(clickedUser)
        ) : selectedUsers?.length === 1 && selectedUsers?.[0] ? (
          displayProfileCard(selectedUsers[0])
        ) : selectedUsers?.length > 1 ? (
          <DialogContent>
            <Grid container spacing={2}>
              {selectedUsers.map((user) => {
                return (
                  <Grid item xs={12} key={user._id}>
                    {displayProfileCard(user)}
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
        ) : null}
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDeleteUserDialog('');
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('pages.Admin.confirmDeleteUserTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('pages.Admin.confirmDeleteUser')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setDeleteUserDialog('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={() => dialogAdminDeleteAccount()} autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
